import React from 'react';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

// Components
import Handler from '../../edge/Handler';

export default function KeywordTriggerPreview({
  node,
  color,
  isConnected,
  onEdgeMouseDown,
  allowToEditFlow,
}) {
  const keywords = node?.data?.keywords || [];
  const matchType = node?.data?.match_type || 'exact';
  const caseSensitive = node?.data?.case_sensitive || false;
  const matchThreshold = node?.data?.match_threshold || 0;
  const phoneNumbers = node?.data?.phone_numbers || [];

  const connection_point = {
    id: node?.data?.id,
    type: 'action',
  };

  return (
    <div className="relative w-full h-full px-3 py-4 bg-white rounded-xl">
      <div className="relative w-full h-full flex flex-col gap-4 p-4 bg-[#F8F9FC] rounded-[10px]">
        <div className="w-full bg-white rounded-xl">
          {/* Body */}
          <div className="p-2">
            {/* Phone Numbers */}
            {phoneNumbers.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3 text-[0.813rem] font-medium text-slate-700">
                  <div className="flex items-center gap-2">
                    {/* <div className="w-2 h-2 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full"></div> */}
                    <span>Phone Numbers</span>
                  </div>
                  <Tooltip title="Phone numbers that will receive the trigger">
                    <QuestionCircleOutlined className="text-slate-400 hover:text-slate-600 transition-colors" />
                  </Tooltip>
                </div>
                <div className="flex flex-col gap-2">
                  {phoneNumbers.slice(0, 2).map((phone, index) => (
                    <div 
                      key={phone.id || index}
                      className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/60 text-emerald-800 text-[0.813rem] px-3 py-2.5 rounded-lg flex flex-col gap-1 hover:shadow-sm transition-all duration-200 hover:border-emerald-300/80"
                    >
                      <span className="font-semibold text-emerald-900">{ `+${phone.country_code}` + ' ' + phone.send_from_phone_number}</span>
                      <span className="text-emerald-600 text-[11px] font-medium">
                        {phone.send_from_phone_name ? `(${phone.send_from_phone_name})` : ''}
                      </span>
                    </div>
                  ))}
                  {phoneNumbers.length > 2 && (
                    <div className="text-[0.813rem] text-slate-500 flex items-center px-3 py-1">
                      <span className="bg-slate-100 px-2 py-1 rounded-md font-medium">
                        +{phoneNumbers.length - 2} more
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Divider */}
            {phoneNumbers.length > 0 && (
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
                </div>
                <div className="relative flex justify-center">
                  <div className="bg-white px-2">
                    <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Keywords */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3 text-[0.813rem] font-medium text-slate-700">
                <div className="flex items-center gap-2">
                  {/* <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full"></div> */}
                  <span>Keywords</span>
                </div>
                <Tooltip title="Keywords that will trigger this sequence">
                  <QuestionCircleOutlined className="text-slate-400 hover:text-slate-600 transition-colors" />
                </Tooltip>
              </div>
              {keywords.length > 0 ? (
                <div className="flex flex-wrap gap-2 max-h-[7rem] overflow-y-auto">
                  {keywords.map((keyword, index) => (
                    <span 
                      key={index}
                      className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 text-blue-800 text-[0.813rem] font-medium px-3 py-1.5 rounded-lg hover:shadow-sm transition-all duration-200 hover:border-blue-300/80 hover:from-blue-100 hover:to-indigo-100"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-3 text-center">
                  <span className="text-[0.813rem] text-slate-400 font-medium">No keywords added</span>
                </div>
              )}
            </div>

            {/* Match Settings */}
            <div className="bg-gradient-to-r from-purple-50/50 to-pink-50/50 border border-purple-200/40 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full"></div> */}
                  <span className="text-[0.813rem] font-medium text-slate-700">Match Type</span>
                </div>
                <div className="font-semibold text-slate-800 flex items-center gap-2">
                  {matchType === 'exact' ? (
                    <span className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 px-3 py-1 rounded-md text-[0.813rem] font-semibold border border-purple-200/60">
                      Exact
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 px-3 py-1 rounded-md text-[0.813rem] font-semibold border border-purple-200/60">
                        Fuzzy
                      </span>
                      {matchType === 'fuzzy' && matchThreshold > 0 && (
                        <Tooltip title={`Minimum ${matchThreshold}% match required`}>
                          <span className="bg-gradient-to-br from-purple-400 to-purple-500 text-white text-[0.813rem] font-semibold px-3 py-1 rounded-md shadow-sm hover:shadow-md transition-all duration-200">
                            {matchThreshold}%
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Handler */}
        <div className="absolute top-1/2 right-[-6px]">
          <Handler
            type="start"
            start_node_id={node?.uniq_id}
            isConnected={isConnected}
            onEdgeMouseDown={({ position, payload }) => {
              if (onEdgeMouseDown) {
                if (!isConnected) {
                  onEdgeMouseDown({
                    connection_point,
                    position: position,
                    payload,
                  });
                }
              }
            }}
            color={color}
            allowToEditFlow={allowToEditFlow}
          />
        </div>
      </div>
    </div>
  );
}